import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DiscordConnectPanel,
  type DiscordConnectPanelStatus,
} from '../components/discord-connect-panel';
import { HTTPStatusError } from '../lib/chat-api';
import { startDiscordConnect } from '../lib/discord';

type StartStatus = Extract<DiscordConnectPanelStatus, 'loading' | 'error'>;

function currentPathWithSearch() {
  return `${window.location.pathname}${window.location.search}`;
}

export function DiscordConnectStartPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StartStatus>('loading');
  const [message, setMessage] = useState('Opening Discord connection...');

  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        const result = await startDiscordConnect({
          dmHandoffToken: searchParams.get('handoff')?.trim() || undefined,
        });
        if (cancelled) {
          return;
        }
        window.location.replace(result.authorization_url);
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof HTTPStatusError && error.status === 401) {
          navigate(
            `/login?next=${encodeURIComponent(currentPathWithSearch())}`,
            {
              replace: true,
            }
          );
          return;
        }
        setStatus('error');
        setMessage(
          error instanceof Error && error.message.trim()
            ? error.message
            : 'Failed to start Discord connection.'
        );
      }
    };

    void start();

    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  return (
    <DiscordConnectPanel
      status={status}
      title={
        status === 'loading' ? 'Opening Discord' : 'Discord connection failed'
      }
      message={message}
      helperText={
        status === 'loading'
          ? 'Bridge is opening Discord so you can confirm the connection.'
          : 'Return to Bridge and try connecting Discord again.'
      }
    />
  );
}
